import { Injectable } from "@angular/core";
import { Observable, Subscriber } from 'rxjs';
import { finalize, switchMap } from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class IndexDbCacheService {
  private readonly dbName: string = 'contact-cloud-db';
  private readonly storeName: string = 'cache';
  /**
   * For remove and recreate cache [this.storeName] please increment [this.dbVersion],
   * if [this.dbVersion] decremented then an error will be thrown
   * 'Error opening indexDB - can't open connection: request.error.message'
   */
  private readonly dbVersion: number = 4;
  private readonly readWriteMode: IDBTransactionMode = 'readwrite';
  private readonly readonlyMode: IDBTransactionMode = 'readonly';

  getData<T>(key: string): Observable<T> {
    return this.getOrCreateDb().pipe(
      switchMap((db: IDBDatabase): Observable<T> =>
        this.createReadTransaction<T>(db, key).pipe(
          finalize(() => this.dbClose(db))
        ))
    );
  }

  setData<T>(key: string, value: T): Observable<T> {
    return this.getOrCreateDb().pipe(
      switchMap((db: IDBDatabase): Observable<T> =>
        this.createWriteTransaction(db, key, value).pipe(
        finalize(() => this.dbClose(db))
      ))
    );
  }

  private createWriteTransaction<T>(db: IDBDatabase, key: string, value: T): Observable<T> {
    return new Observable<T>((subscriber: Subscriber<T>) => {
      const transaction: IDBTransaction = db.transaction([this.storeName], this.readWriteMode);

      transaction.onerror = () => {
        subscriber.error(new Error("Error writing to table: " + transaction.error.message));
      };

      const objectStore: IDBObjectStore = transaction.objectStore(this.storeName);

      const cursorRequest: IDBRequest<IDBCursorWithValue> = objectStore.openCursor(key);

      cursorRequest.onsuccess = function () {
        if (cursorRequest.result) {
          cursorRequest.result.update(value);

          subscriber.next(value);
          subscriber.complete();
        } else {
          try {
            const request: IDBRequest = objectStore.add(value, key);

            request.onsuccess = () => {
              subscriber.next(value);
              subscriber.complete();
            };

            request.onerror = () => {
              subscriber.error(new Error("Error writing from table: " + request.error.message));
            };
          } catch (error) {
            subscriber.error(new Error("Error writing from table: " + error.message));
          }
        }
      };

      cursorRequest.onerror = function () {
        subscriber.error(new Error("Error writing from table: " + cursorRequest.error.message));
      };

    });
  }

  private createReadTransaction<T>(db: IDBDatabase, key: string): Observable<T> {
    return new Observable<T>((subscriber: Subscriber<T>) => {
      const transaction: IDBTransaction = db.transaction([this.storeName], this.readonlyMode);

      transaction.onerror = () => {
        subscriber.error(new Error("Error reading from table: " + transaction.error.message));
      };

      const objectStore: IDBObjectStore = transaction.objectStore(this.storeName);

      try {

        const request: IDBRequest = objectStore.get(key);

        request.onsuccess = () => {
          subscriber.next(request.result || null);
          subscriber.complete();
        };

        request.onerror = () => {
          subscriber.error(new Error("Error reading from table: " + request.error.message));
        };
      } catch (error) {
        subscriber.error(new Error("Error reading from table: " + error.message));
      }
    });
  }

  private getOrCreateDb(): Observable<IDBDatabase> {
    return new Observable<IDBDatabase>((subscriber: Subscriber<IDBDatabase>) => {
      const request: IDBOpenDBRequest = window.indexedDB.open(this.dbName, this.dbVersion);

      request.onblocked = () => {
        subscriber.error(new Error("Error opening indexDB - blocked: " + request.error.message));
      }
      request.onerror = () => {
        subscriber.error(new Error("Error opening indexDB - can't open connection: " + request.error.message));
      }
      request.onsuccess = () => {
        subscriber.next(request.result);
        subscriber.complete();
      }
      request.onupgradeneeded = () => {
        const db: IDBDatabase = request.result;

        db.onerror = () => {
          subscriber.error(new Error("Error creating table"));
        };

        if (db.objectStoreNames.contains(this.storeName)) {
          db.deleteObjectStore(this.storeName);
        }

        db.createObjectStore(this.storeName);
      }
    });
  }

  private dbClose(db: IDBDatabase): void {
    try {
      db.close();
    } catch (error) {
      /** do nothing **/
    }
  }
}
